import React, { useState, useCallback, useMemo, useRef, useEffect } from 'react';
import { Box, Checkbox, Typography } from '@mui/material';
import { useVirtualScroll } from './VirtualScrollEngine';
import VirtualDataGridRow from './VirtualDataGridRow';
import VirtualDataGridHeader from './VirtualDataGridHeader';

/**
 * High-performance Virtual DataGrid
 * Handles 1000+ rows without pagination using efficient virtualization
 */
const VirtualDataGrid = ({
  data = [],
  columns = [],
  height = 600,
  rowHeight = 52,
  headerHeight = 56,
  checkboxSelection = false,
  onRowClick,
  onRowSelectionChange,
  getRowId = (row) => row.id,
  sortModel,
  onSortModelChange,
  density = 'standard', // compact, standard, comfortable
  loading = false,
  noRowsText = 'No rows to display'
}) => {
  const containerRef = useRef(null);
  const [selectedRows, setSelectedRows] = useState(new Set());
  const [columnWidths, setColumnWidths] = useState(new Map());

  // Calculate row height based on density
  const calculatedRowHeight = useMemo(() => {
    switch (density) {
      case 'compact': return 36;
      case 'comfortable': return 64;
      default: return rowHeight;
    }
  }, [density, rowHeight]);

  // Memoized sorted data
  const sortedData = useMemo(() => {
    if (!sortModel?.length || !data.length) return data;

    return [...data].sort((a, b) => {
      for (const sort of sortModel) {
        const { field, sort: direction } = sort;

        // Find the column definition to check for valueGetter
        const column = columns.find(col => col.field === field);

        // Get values using valueGetter if available, otherwise use direct field access
        const aValue = column?.valueGetter ? column.valueGetter({ row: a }) : a[field];
        const bValue = column?.valueGetter ? column.valueGetter({ row: b }) : b[field];

        if (aValue < bValue) return direction === 'asc' ? -1 : 1;
        if (aValue > bValue) return direction === 'asc' ? 1 : -1;
      }
      return 0;
    });
  }, [data, sortModel, columns]);

  // Virtual scrolling engine
  const {
    visibleItems,
    scrollTop,
    totalHeight,
    handleScroll,
    scrollToItem,
    startIndex,
    endIndex,
    metrics
  } = useVirtualScroll(sortedData, {
    itemHeight: calculatedRowHeight,
    containerHeight: height - headerHeight,
    buffer: 3
  });

  // Calculate column widths
  const calculatedColumns = useMemo(() => {
    return columns.map(col => ({
      ...col,
      width: columnWidths.get(col.field) || col.width || 120,
      minWidth: col.minWidth || 80
    }));
  }, [columns, columnWidths]);

  // Handle row selection
  const handleRowSelection = useCallback((rowId, selected) => {
    const newSelected = new Set(selectedRows);
    if (selected) {
      newSelected.add(rowId);
    } else {
      newSelected.delete(rowId);
    }
    setSelectedRows(newSelected);
    onRowSelectionChange?.(Array.from(newSelected));
  }, [selectedRows, onRowSelectionChange]);

  // Handle select all
  const handleSelectAll = useCallback((selected) => {
    if (selected) {
      const allIds = new Set(sortedData.map(getRowId));
      setSelectedRows(allIds);
      onRowSelectionChange?.(Array.from(allIds));
    } else {
      setSelectedRows(new Set());
      onRowSelectionChange?.([]);
    }
  }, [sortedData, getRowId, onRowSelectionChange]);

  // Handle scroll events
  const handleScrollEvent = useCallback((e) => {
    const scrollTop = e.target.scrollTop;
    handleScroll(scrollTop);
  }, [handleScroll]);

  // Handle column resize
  const handleColumnResize = useCallback((field, width) => {
    setColumnWidths(prev => new Map(prev.set(field, width)));
  }, []);

  // Check if all rows are selected
  const isAllSelected = sortedData.length > 0 && selectedRows.size === sortedData.length;
  const isIndeterminate = selectedRows.size > 0 && selectedRows.size < sortedData.length;

  return (
    <Box
      sx={{
        height,
        width: '100%',
        border: '1px solid rgba(224, 224, 224, 1)',
        borderRadius: 1,
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
        backgroundColor: 'background.paper'
      }}
    >
      {/* Header */}
      <VirtualDataGridHeader
        columns={calculatedColumns}
        checkboxSelection={checkboxSelection}
        isAllSelected={isAllSelected}
        isIndeterminate={isIndeterminate}
        onSelectAll={handleSelectAll}
        sortModel={sortModel}
        onSortModelChange={onSortModelChange}
        onColumnResize={handleColumnResize}
        height={headerHeight}
      />

      {/* Virtual Scrolling Container */}
      <Box
        ref={containerRef}
        sx={{
          flex: 1,
          overflow: 'auto',
          position: 'relative',
          '&::-webkit-scrollbar': {
            width: 12,
            height: 12
          },
          '&::-webkit-scrollbar-track': {
            backgroundColor: 'rgba(0,0,0,0.1)'
          },
          '&::-webkit-scrollbar-thumb': {
            backgroundColor: 'rgba(0,0,0,0.3)',
            borderRadius: 6
          }
        }}
        onScroll={handleScrollEvent}
      >
        {loading ? (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              height: '100%'
            }}
          >
            <Typography>Loading...</Typography>
          </Box>
        ) : sortedData.length === 0 ? (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              height: '100%'
            }}
          >
            <Typography color="textSecondary">{noRowsText}</Typography>
          </Box>
        ) : (
          <>
            {/* Spacer for virtual scrolling */}
            <Box sx={{ height: totalHeight, position: 'relative' }}>
              {/* Visible rows */}
              {visibleItems.map((item) => {
                const rowId = getRowId(item);
                const isSelected = selectedRows.has(rowId);

                return (
                  <VirtualDataGridRow
                    key={rowId}
                    row={item}
                    columns={calculatedColumns}
                    rowHeight={calculatedRowHeight}
                    top={item.top}
                    checkboxSelection={checkboxSelection}
                    isSelected={isSelected}
                    onRowClick={onRowClick}
                    onSelectionChange={(selected) => handleRowSelection(rowId, selected)}
                    getRowId={getRowId}
                  />
                );
              })}
            </Box>
          </>
        )}
      </Box>

      {/* Performance metrics (development only) */}
      {process.env.NODE_ENV === 'development' && (
        <Box
          sx={{
            px: 2,
            py: 0.5,
            backgroundColor: 'grey.50',
            borderTop: '1px solid rgba(224, 224, 224, 1)',
            fontSize: '0.75rem',
            color: 'text.secondary'
          }}
        >
          Virtualized: {metrics.visibleItems}/{metrics.totalItems} rows
          ({metrics.renderRatio}) | {metrics.memoryEfficiency}
        </Box>
      )}
    </Box>
  );
};

export default React.memo(VirtualDataGrid);